import type { CorreoProspect, FichaProspect } from "./tipos";

/**
 * «Para llamar» (ficha del setter, contrato-crm-llamadas.md §10, Robert 08/10/2026): lo que
 * se calcula de la ficha de Nevora Prospect para enseñarla igual que en Prospect. Solo lógica;
 * los textos van en el catálogo (nevora.para_llamar.*).
 */

/** Tipos de lead que el catálogo sabe nombrar; el resto se enseña tal cual llega. */
export const TIPOS_LEAD = [
  "email_sin_respuesta",
  "sin_email",
  "solo_llamadas",
  "frio_sin_analisis",
] as const;

export const tipoLeadConocido = (tipo?: string | null): boolean =>
  !!tipo && (TIPOS_LEAD as readonly string[]).includes(tipo);

/** Tipos de correo con nombre propio; el resto, «Correo». */
export const TIPOS_CORREO = [
  "toque_1",
  "toque_2",
  "toque_3",
  "recordatorio",
  "manual",
] as const;

export const tipoCorreoConocido = (tipo?: string | null): boolean =>
  !!tipo && (TIPOS_CORREO as readonly string[]).includes(tipo);

/** Los correos en orden de fecha (los que no traen fecha, al final). */
export const correosOrdenados = (
  ficha?: FichaProspect | null,
): CorreoProspect[] =>
  [...(Array.isArray(ficha?.correos) ? ficha.correos : [])].sort((a, b) =>
    (a.enviado_at ?? "9999").localeCompare(b.enviado_at ?? "9999"),
  );

/**
 * Línea de arriba del bloque «Correo enviado» (§10.3): cuántos y si hubo recordatorio, con la
 * fecha del primer recordatorio. null si la ficha no trae la lista de correos (fichas
 * anteriores al 08/10: entonces vale `email_nevora`).
 */
export const resumenCorreos = (
  ficha?: FichaProspect | null,
): { n: number; recordatorio: string | null } | null => {
  if (!Array.isArray(ficha?.correos)) return null;
  const correos = correosOrdenados(ficha);
  const recordatorio = correos.find((c) => c.tipo === "recordatorio");
  return {
    n: correos.length,
    recordatorio: recordatorio ? (recordatorio.enviado_at ?? "") : null,
  };
};

/**
 * Para la línea de la cabecera «Correo enviado el día X desde el buzón Y» (Robert, 09/10/2026):
 * el primer correo que salió. Con fichas anteriores al 08/10 solo hay la fecha (email_nevora).
 */
export const primerCorreo = (
  ficha?: FichaProspect | null,
): { fecha: string; buzon: string | null } | null => {
  const primero = correosOrdenados(ficha).find((c) => c.enviado_at);
  if (primero?.enviado_at) {
    return { fecha: primero.enviado_at, buzon: primero.buzon ?? null };
  }
  if (ficha?.email_nevora?.enviado && ficha.email_nevora.fecha) {
    return { fecha: ficha.email_nevora.fecha, buzon: null };
  }
  return null;
};

/** 36: quién firma cada buzón de Nevora (robert@ → Robert, jan@ → Jan). */
const FIRMANTES: Record<string, string> = { robert: "Robert", jan: "Jan" };

/**
 * 36 (punto 10): quién firma el correo. El que diga Prospect (correos_de_negocio) y, si no, el del
 * buzón desde el que salió. null si no se sabe.
 */
export const firmanteDe = (
  c: Pick<CorreoProspect, "buzon" | "firmante">,
): string | null => {
  if (c.firmante?.trim()) return c.firmante.trim();
  const local = c.buzon?.split("@")[0]?.trim().toLowerCase();
  return (local && FIRMANTES[local]) || null;
};

/** 36: a partir de cuántos días del primer correo no se abre la llamada con él (Robert, 20/10). */
export const DIAS_CORREO_LEJANO = 7;

/** 36: ¿hace más de 7 días del primer correo? Entonces, «No abrir con el correo». */
export const correoLejano = (
  ficha: FichaProspect | null | undefined,
  ahora: Date,
  dias = DIAS_CORREO_LEJANO,
): boolean => {
  const primero = primerCorreo(ficha);
  if (!primero) return false;
  const enviado = new Date(primero.fecha).getTime();
  if (Number.isNaN(enviado)) return false;
  return ahora.getTime() - enviado > dias * 24 * 60 * 60 * 1000;
};

/**
 * 36: los correos de prospect.correos_de_negocio, con la forma de los de la ficha. Si Prospect los
 * da, mandan sobre los que trae la ficha (son los de verdad, con su firmante).
 */
export const correosDeProspect = (
  filas: {
    momento: string;
    que: string;
    asunto: string | null;
    texto: string | null;
    firmante: string | null;
  }[],
): CorreoProspect[] =>
  filas.map((f) => ({
    tipo: f.que,
    asunto: f.asunto,
    cuerpo: f.texto,
    enviado_at: f.momento,
    firmante: f.firmante,
  }));

/** ¿Hay algo del análisis de su web que enseñar? */
export const hayAnalisis = (ficha?: FichaProspect | null): boolean => {
  const a = ficha?.analisis;
  return (
    !!a &&
    (!!a.resumen ||
      !!a.sector?.veredicto ||
      (a.senales?.length ?? 0) > 0 ||
      (a.hechos?.length ?? 0) > 0)
  );
};

/** Capturas que la ficha declara (la función `capturas` del CRM decide cuáles firma). */
export const numeroCapturas = (ficha?: FichaProspect | null): number =>
  Array.isArray(ficha?.capturas) ? ficha.capturas.length : 0;

/** Resumen para abrir la llamada: el de la ficha (3-5 líneas) o, si falta, el texto plano. */
export const resumenParaLlamar = (
  ficha?: FichaProspect | null,
  resumenPlano?: string | null,
): string | null => ficha?.resumen?.trim() || resumenPlano?.trim() || null;
